import { createBrowserRouter, Navigate, useParams } from 'react-router-dom';
import { App } from './App';
import { LoginPage } from '../pages/LoginPage';
import { ForgotPasswordPage } from '../pages/ForgotPasswordPage';
import { ResetPasswordPage } from '../pages/ResetPasswordPage';
import { CustomerConsolePage } from '../pages/CustomerConsolePage';
import { SmartServicePage } from '../pages/SmartServicePage';
import { SupplierConsolePage } from '../pages/SupplierConsolePage';
import { SmartAdjustPage } from '../features/settings/SmartAdjustPage';
import { ProductListPage } from '../pages/ProductListPage';
import { ProductFormPage } from '../pages/ProductFormPage';
import { InvoicesPage } from '../pages/InvoicesPage';
import { CertificatePage } from '../pages/CertificatePage';
import { AccountingPage } from '../pages/AccountingPage';
import { OrderPrintPage } from '../features/sales-orders/OrderPrintPage';
import { PurchaseOrderPrintPage } from '../features/purchase-orders/PurchaseOrderPrintPage';
import { SettingsLayout } from '../pages/SettingsLayout';
import { DashboardPage } from '../pages/DashboardPage';
import { ProtectedRoute } from '../features/auth/ProtectedRoute';
import { AdminOnlyRoute } from '../features/auth/AdminOnlyRoute';
import { SETTINGS_MODULES } from '../features/settings/modules';

/**
 * REQ-20260418015：把舊的 List / Detail / Edit / New 四頁合併到 /customers/console。
 */
function LegacyCustomerRedirect(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/customers/console?id=${id ?? ''}`} replace />;
}

/** REQ-20260522 Phase 1B-Console:supplier list / form / edit 全合到 /suppliers/console */
function LegacySupplierRedirect(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/suppliers/console?id=${id ?? ''}`} replace />;
}

export const router: ReturnType<typeof createBrowserRouter> = createBrowserRouter([
  {
    path: '/',
    element: <App />,
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      { path: 'login', element: <LoginPage /> },
      { path: 'forgot-password', element: <ForgotPasswordPage /> },
      { path: 'reset-password', element: <ResetPasswordPage /> },
      {
        element: <ProtectedRoute />,
        children: [
          { path: 'dashboard', element: <DashboardPage /> },
          {
            path: 'customers',
            element: <Navigate to="/customers/console" replace />,
          },
          {
            path: 'customers/new',
            element: <Navigate to="/customers/console?mode=new" replace />,
          },
          { path: 'customers/console', element: <CustomerConsolePage /> },
          { path: 'smart-service', element: <SmartServicePage /> },
          // REQ-20260520 ERP Phase 1B → REQ-20260522 Console
          {
            path: 'suppliers',
            element: <Navigate to="/suppliers/console" replace />,
          },
          {
            path: 'suppliers/new',
            element: <Navigate to="/suppliers/console?mode=new" replace />,
          },
          { path: 'suppliers/console', element: <SupplierConsolePage /> },
          { path: 'suppliers/:id', element: <LegacySupplierRedirect /> },
          { path: 'suppliers/:id/edit', element: <LegacySupplierRedirect /> },
          // REQ-20260520 ERP Phase 1C
          { path: 'products', element: <ProductListPage /> },
          { path: 'products/new', element: <ProductFormPage /> },
          { path: 'products/:id/edit', element: <ProductFormPage /> },
          // REQ-20260602001 電子發票
          { path: 'accounting', element: <AccountingPage /> },
          // REQ-20260806003 藝品印刷訂單(既有出貨單的列印呈現,不是新資料表)
          { path: 'sales-orders/:id/print', element: <OrderPrintPage /> },
          // REQ-20260814001 進貨單列印(同樣是既有單據的唯讀呈現,不是新資料表)
          { path: 'purchase-orders/:id/print', element: <PurchaseOrderPrintPage /> },
          { path: 'invoices', element: <InvoicesPage /> },
          { path: 'invoices/:id/certificate', element: <CertificatePage /> },
          { path: 'customers/:id', element: <LegacyCustomerRedirect /> },
          {
            path: 'customers/:id/edit',
            element: <LegacyCustomerRedirect />,
          },
          {
            element: <AdminOnlyRoute />,
            children: [
              // REQ-20260523:我獨自升級(AI)— 從 /settings 移到 top-level
              { path: 'smart-adjust', element: <SmartAdjustPage /> },
              {
                path: 'settings',
                element: <SettingsLayout />,
                // REQ-20260419003：路由從 registry 動態生成
                children: SETTINGS_MODULES.map((m) => ({
                  path: m.path,
                  element: m.element,
                })),
              },
            ],
          },
        ],
      },
    ],
  },
]);
